import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectMipsReweighting } from '@store/selectors/mips-packet/mips-packet.selector';
import { CategoryWeights, ReweightingResult } from '@models/mips-reweighting.model';
import { MipsQualityAttestationInput } from '@models/mips-reweighting-score.model';

export type { CategoryWeights, ReweightingResult };

/** Shared shape-builder for CalculateMipsReweightingRequest's traditional/mvp inputs — callers map their own category totals into these primitives since the dashboard's DashboardMipsScoreBlock and the packet's QualityCategory/PromotingInteroperability/ImprovementActivities carry the same values under different field names. */
export function buildQualityAttestationInput(
  qualityPoints: number,
  qualityTotal: number,
  qualityMaxPoints: number,
  iaTotal: number | null,
  piTotal: number | null,
): MipsQualityAttestationInput {
  return { qualityPoints, qualityTotal, qualityMaxPoints, iaTotal, piTotal };
}

const BASE_WEIGHTS: CategoryWeights = {
  quality: 30,
  cost: 30,
  ia: 15,
  pi: 25,
};

const EMPTY_RESULT: ReweightingResult = {
  weights: BASE_WEIGHTS,
  isReweighted: false,
  reweightedCategories: [],
  explanationTooltip: '',
};

/**
 * Thin proxy over the mips-packet store's reweighting state, which is populated by calling the
 * API's GetMipsReweighting endpoint (MipsReweightingService.cs owns the CMS scenario table —
 * this service no longer keeps its own copy).
 */
@Injectable({
  providedIn: 'root'
})
export class MipsReweightingService {
  private readonly store = inject(Store);

  getReweighting(): Observable<ReweightingResult> {
    return this.store.select(selectMipsReweighting).pipe(
      map((result) => result ?? EMPTY_RESULT)
    );
  }

  getReweightingSignal() {
    return toSignal(this.getReweighting(), { initialValue: EMPTY_RESULT });
  }

  getMaxWeightForCategory(category: 'quality' | 'pi' | 'ia'): Observable<number> {
    return this.getReweighting().pipe(
      map(result => result.weights[category])
    );
  }
}
